import { apiClient } from './apiClient';
import {
  User,
  UserProfile,
  UserPreferences,
  UserStats,
} from '@coffeeplexity/shared';

// FIXED: Updated to match backend UserProfileUpdate schema
// Backend expects individual *_url fields, not nested social_links object
export interface UpdateProfileData {
  first_name?: string;
  last_name?: string;
  display_name?: string;
  bio?: string | null;
  location?: string | null;
  website_url?: string | null;
  // Individual social media URL fields matching backend schema
  instagram_url?: string | null;
  twitter_url?: string | null;
  youtube_url?: string | null;
  tiktok_url?: string | null;
}

export interface UpdatePreferencesData {
  email_notifications?: boolean;
  push_notifications?: boolean;
  privacy_level?: 'public' | 'private' | 'friends';
  preferred_units?: 'metric' | 'imperial';
  theme?: 'light' | 'dark' | 'auto';
}

export interface ChangePasswordData {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

export interface UploadAvatarResponse {
  avatar_url: string;
  message: string;
}

export const userApi = {
  // Get user profile
  getProfile: async (userId: string): Promise<UserProfile> => {
    if (!userId) {
      throw new Error('User ID is required to fetch profile');
    }

    const response = await apiClient.get(`/users/${userId}/profile`);
    return response.data;
  },

  // Update user profile
  updateProfile: async (
    userId: string,
    data: UpdateProfileData
  ): Promise<User> => {
    const response = await apiClient.put(`/users/${userId}/profile`, data);
    return response.data;
  },

  // Get user preferences
  getPreferences: async (userId: string): Promise<UserPreferences> => {
    const response = await apiClient.get(`/users/${userId}/preferences`);
    return response.data;
  },

  // Update user preferences
  updatePreferences: async (
    userId: string,
    data: UpdatePreferencesData
  ): Promise<UserPreferences> => {
    const response = await apiClient.put(`/users/${userId}/preferences`, data);
    return response.data;
  },

  // Get user statistics
  getStats: async (userId: string): Promise<UserStats> => {
    const response = await apiClient.get(`/users/${userId}/stats`);
    return response.data;
  },

  // Upload avatar
  uploadAvatar: async (
    userId: string,
    imageUri: string
  ): Promise<UploadAvatarResponse> => {
    const formData = new FormData();
    formData.append('avatar', {
      uri: imageUri,
      type: 'image/jpeg',
      name: 'avatar.jpg',
    } as any);

    const response = await apiClient.post(`/users/${userId}/avatar`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Delete avatar
  deleteAvatar: async (userId: string): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/users/${userId}/avatar`);
    return response.data;
  },

  // Change password
  changePassword: async (
    userId: string,
    data: ChangePasswordData
  ): Promise<{ message: string }> => {
    const response = await apiClient.put(`/users/${userId}/password`, data);
    return response.data;
  },

  // Delete account
  deleteAccount: async (
    userId: string,
    password: string
  ): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/users/${userId}`, {
      data: { password },
    });
    return response.data;
  },

  // Get user's recipes
  getUserRecipes: async (userId: string, page = 1, limit = 20) => {
    const response = await apiClient.get(`/users/${userId}/recipes`, {
      params: { page, limit },
    });
    return response.data;
  },

  // Get user's favorites
  getUserFavorites: async (userId: string, page = 1, limit = 20) => {
    const response = await apiClient.get(`/users/${userId}/favorites`, {
      params: { page, limit },
    });
    return response.data;
  },

  // Get user's reviews
  getUserReviews: async (userId: string, page = 1, limit = 20) => {
    const response = await apiClient.get(`/users/${userId}/reviews`, {
      params: { page, limit },
    });
    return response.data;
  },

  // Export user data (GDPR compliance)
  exportUserData: async (
    userId: string
  ): Promise<{
    download_url: string;
    expires_at: string;
  }> => {
    const response = await apiClient.post(`/users/${userId}/export`);
    return response.data;
  },

  // Get privacy settings
  getPrivacySettings: async (userId: string) => {
    const response = await apiClient.get(`/users/${userId}/privacy`);
    return response.data;
  },

  // Update privacy settings
  updatePrivacySettings: async (userId: string, settings: any) => {
    const response = await apiClient.put(`/users/${userId}/privacy`, settings);
    return response.data;
  },
};
